import { ExternalLink, FileText } from "lucide-react";
import type { BillingAccount, BillingAccountDetail } from "@shared/lib/admin-billing";
import { useView } from "@shared/client/view";
import { Link } from "@shared/client/navigation";
import { formatCents, formatDate, formatDateTime, formatNumber } from "@shared/lib/format";
import { AdminEmpty, StatCard, StatGroup, StatusDot } from "@shared/components/blocks";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogClose, DialogHeader, DialogTitle } from "@shared/components/ui/dialog";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import { Skeleton } from "@shared/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { AccountAvatar, UsageMeter, displayName, priceLabel, stripeCustomerUrl, stripeState } from "./billing-parts";

const INVOICE_STATUS: Record<string, { label: string; tone: string }> = {
  paid: { label: "Payée", tone: "active" },
  open: { label: "À payer", tone: "trial" },
  draft: { label: "Brouillon", tone: "neutral" },
  uncollectible: { label: "Irrécouvrable", tone: "danger" },
  void: { label: "Annulée", tone: "neutral" },
};

/** Une ligne « libellé … valeur » d'une liste de définitions. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b py-2.5 text-[13.5px] last:border-b-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="m-0 min-w-0 text-right">{children}</dd>
    </div>
  );
}

function UsageCell({ label, note, children }: { label: string; note?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="min-w-0 border-r border-b px-4 py-3.5 max-sm:px-3 max-sm:py-3">
      <span className="block text-[12.5px] text-muted-foreground">{label}</span>
      <div className="mt-2">{children}</div>
      {note && <small className="mt-1.5 block truncate text-[12px] text-subtle-foreground">{note}</small>}
    </div>
  );
}

function Information({ account, detail }: { account: BillingAccount; detail: BillingAccountDetail | undefined }) {
  const state = stripeState(account);
  const price = priceLabel(account);
  const costShare = account.mrr_cents > 0 ? Math.round((account.ai_cost_cents / account.mrr_cents) * 100) : null;
  return (
    <div className="grid gap-6">
      <div className="grid gap-x-8 gap-y-6 md:grid-cols-2">
        <section>
          <h3 className="mb-1 text-[14px] font-semibold">Abonnement</h3>
          <dl className="m-0">
            <Field label="Plan appliqué">
              {account.plan_name}
              {account.is_admin && <span className="text-muted-foreground"> · admin</span>}
            </Field>
            <Field label="Prix">{price ?? (account.is_admin ? "Offert (admin)" : "Gratuit")}</Field>
            <Field label="Statut"><StatusDot tone={state.tone}>{state.label}</StatusDot></Field>
            <Field label="Abonné depuis">{account.stripe_status ? formatDate(account.subscribed_at) : "—"}</Field>
            <Field label={account.cancel_at_period_end ? "Fin de l’abonnement" : "Prochaine échéance"}>
              {formatDate(account.current_period_end)}
            </Field>
            <Field label="Compte créé le">{formatDate(account.created_at)}</Field>
            <Field label="Dernière connexion">{formatDateTime(account.last_sign_in_at)}</Field>
          </dl>
        </section>
        <section>
          <h3 className="mb-1 text-[14px] font-semibold">Stripe</h3>
          {account.stripe_customer_id ? (
            <>
              <dl className="m-0">
                <Field label="Customer ID"><code className="font-mono text-[12.5px]">{account.stripe_customer_id}</code></Field>
                <Field label="Subscription ID">
                  <code className="font-mono text-[12.5px] break-all">{account.stripe_subscription_id ?? "—"}</code>
                </Field>
                <Field label="Facturation">
                  {account.billing_interval === "yearly" ? "Annuelle" : account.billing_interval === "monthly" ? "Mensuelle" : "—"}
                </Field>
              </dl>
              <Button asChild className="mt-3" size="sm" variant="outline">
                <a href={stripeCustomerUrl(account.stripe_customer_id)} rel="noreferrer" target="_blank">
                  <ExternalLink /> Voir sur Stripe
                </a>
              </Button>
            </>
          ) : (
            <p className="m-0 py-2.5 text-[13.5px] text-muted-foreground">
              Aucun client Stripe : ce compte ne s’est jamais abonné.
            </p>
          )}
          {account.client && (
            <p className="mt-4 mb-0 text-[13.5px] text-muted-foreground">
              Client rattaché :{" "}
              <Link className="text-primary underline-offset-4 hover:underline" href={`/admin/clients/${account.client.id}`}>
                {account.client.name}
              </Link>
            </p>
          )}
        </section>
      </div>

      <section>
        <h3 className="mb-3 text-[14px] font-semibold">Consommation sur 30 jours</h3>
        <StatGroup columns={4}>
          <UsageCell label="Offres suivies" note={`Limite du plan ${account.plan_name}`}>
            <UsageMeter max={account.max_offers} used={account.offers_used} />
          </UsageCell>
          <UsageCell
            label="Passages"
            note={account.manual_passes > 0 ? `dont ${account.manual_passes} lancé${account.manual_passes > 1 ? "s" : ""} à la main` : `veille tous les ${account.min_frequency_days} j au plus`}
          >
            <UsageMeter max={account.pass_capacity} used={account.passes} />
          </UsageCell>
          <StatCard label="Leads trouvés" note="nouvelles organisations" value={formatNumber(account.leads)} />
          <StatCard
            label="Coût IA"
            note={costShare !== null ? `${costShare} % du MRR du compte` : detail ? `${formatNumber(detail.costs.monthCalls)} appels ce mois-ci` : undefined}
            tint={costShare !== null && costShare > 50 ? "rose" : undefined}
            value={formatCents(account.ai_cost_cents)}
          />
        </StatGroup>
        {detail && detail.costs.breakdown.length > 0 && (
          <p className="mt-2.5 mb-0 text-[12.5px] text-muted-foreground">
            Par fournisseur : {detail.costs.breakdown.map((item) => `${item.provider} ${formatCents(item.costCents)}`).join(" · ")}
          </p>
        )}
      </section>
    </div>
  );
}

function Offers({ detail }: { detail: BillingAccountDetail }) {
  if (detail.offers.length === 0) return <AdminEmpty>Aucune offre créée : l’onboarding n’est pas allé au bout.</AdminEmpty>;
  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>Offre</TableHead>
          <TableHead>Veille</TableHead>
          <TableHead className="max-sm:hidden">Dernier passage</TableHead>
          <TableHead className="text-right">Passages</TableHead>
          <TableHead className="text-right">Leads</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {detail.offers.map((offer) => (
          <TableRow key={offer.id}>
            <TableCell className="max-w-[260px]">
              <span className="block truncate font-medium" title={offer.name}>{offer.name}</span>
              <span className="block truncate text-[12px] text-muted-foreground">{offer.url}</span>
            </TableCell>
            <TableCell>
              {offer.archived_at ? (
                <StatusDot tone="neutral">Archivée</StatusDot>
              ) : offer.watch_active ? (
                <StatusDot tone="active">Tous les {offer.frequency_days} j</StatusDot>
              ) : (
                <StatusDot tone="lead">Coupée</StatusDot>
              )}
            </TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums max-sm:hidden">
              {formatDate(offer.last_run_at)}
            </TableCell>
            <TableCell className="text-right tabular-nums">{offer.passes}</TableCell>
            <TableCell className="text-right tabular-nums">{offer.leads}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Invoices({ detail }: { detail: BillingAccountDetail }) {
  if (detail.invoicesError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Factures indisponibles : {detail.invoicesError}</AlertDescription>
      </Alert>
    );
  }
  if (!detail.invoices?.length) return <AdminEmpty>Aucune facture Stripe pour ce compte.</AdminEmpty>;
  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>Facture</TableHead>
          <TableHead className="max-sm:hidden">Date</TableHead>
          <TableHead className="text-right">Montant</TableHead>
          <TableHead>Statut</TableHead>
          <TableHead className="w-10"><span className="sr-only">PDF</span></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {detail.invoices.map((invoice) => {
          const status = INVOICE_STATUS[invoice.status ?? ""] ?? { label: invoice.status ?? "—", tone: "neutral" };
          return (
            <TableRow key={invoice.id}>
              <TableCell>
                <span className="font-medium">{invoice.number ?? "Brouillon"}</span>
                <span className="block text-[12px] text-muted-foreground sm:hidden">{formatDate(invoice.created_at)}</span>
              </TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground tabular-nums max-sm:hidden">
                {formatDate(invoice.created_at)}
              </TableCell>
              <TableCell className="text-right tabular-nums">{formatCents(invoice.amount_cents)}</TableCell>
              <TableCell><StatusDot tone={status.tone}>{status.label}</StatusDot></TableCell>
              <TableCell>
                {(invoice.pdf_url ?? invoice.hosted_url) && (
                  <Button asChild aria-label={`Ouvrir la facture ${invoice.number ?? ""}`} size="icon" variant="ghost">
                    <a href={invoice.pdf_url ?? invoice.hosted_url ?? undefined} rel="noreferrer" target="_blank">
                      <FileText className="text-muted-foreground" />
                    </a>
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** Fiche facturation d'un compte : la ligne du tableau, puis le détail chargé à l'ouverture. */
export function BillingAccountDialog({ account, onClose }: { account: BillingAccount | null; onClose: () => void }) {
  const { data: detail, error } = useView<BillingAccountDetail>(
    account ? `/api/views/admin/billing/${account.user_id}` : null,
  );
  const shown = detail?.account ?? account;

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={account !== null}>
      <DialogContent className="flex max-h-[min(860px,calc(100dvh-2rem))] flex-col gap-0 p-0 sm:max-w-4xl">
        {shown && (
          <>
            <DialogHeader className="flex-row items-center gap-3.5 px-6 pt-6 pb-4 text-left">
              <AccountAvatar account={shown} className="size-11" />
              <div className="min-w-0">
                <DialogTitle className="truncate text-[17px] font-semibold">
                  {shown.client?.name ?? displayName(shown)}
                </DialogTitle>
                <DialogDescription className="truncate">{shown.email}</DialogDescription>
              </div>
            </DialogHeader>
            <Tabs className="flex min-h-0 flex-1 flex-col gap-0" defaultValue="info">
              <div className="border-b px-6 pb-3">
                <TabsList>
                  <TabsTrigger value="info">Informations</TabsTrigger>
                  <TabsTrigger value="offers">Offres{detail ? ` (${detail.offers.length})` : ""}</TabsTrigger>
                  <TabsTrigger value="invoices">Factures{detail?.invoices ? ` (${detail.invoices.length})` : ""}</TabsTrigger>
                </TabsList>
              </div>
              <ScrollArea className="min-h-0 flex-1">
                <div className="px-6 py-5">
                  {error && (
                    <Alert className="mb-4" variant="destructive">
                      <AlertDescription>{error instanceof Error ? error.message : "Détail indisponible."}</AlertDescription>
                    </Alert>
                  )}
                  <TabsContent value="info">
                    <Information account={shown} detail={detail} />
                  </TabsContent>
                  <TabsContent value="offers">
                    {detail ? <Offers detail={detail} /> : <Skeleton className="h-32 w-full" />}
                  </TabsContent>
                  <TabsContent value="invoices">
                    {detail ? <Invoices detail={detail} /> : <Skeleton className="h-32 w-full" />}
                  </TabsContent>
                </div>
              </ScrollArea>
            </Tabs>
            <DialogFooter className="border-t px-6 py-3">
              <DialogClose asChild>
                <Button variant="ghost">Fermer</Button>
              </DialogClose>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
